import { Box, Divider, IconButton } from '@mui/material'

import { ReactComponent as DownloadLogo } from 'assets/analytics_download_logo.svg'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import Input from 'components/shared/textFields/Input'
import useExportFatnessReport from 'hook/query/useExportFatnessReport'
import { useDebounce } from 'hook/useDebounce'
import useCowsNextPage from 'hook/useNextPage'
import { useVisibleCowsCount } from 'hook/useVisibleCowsCount'
import { useEffect, useMemo, useState } from 'react'
import { defaultInitialState } from 'store/lamenessFilterSlice'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import CustomPagination from 'ui/CustomPagination'
import Preloader from 'ui/Preloader'
import TooltipHover from 'ui/TooltipHover'
import TabsFilter from 'ui/breadCrumbs/TabsFilter'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import FatnessTable from '../shared/FatnessTable'
import { filterCowsWithFarmCowNumber } from '../../cattlesPage/shared/cattleListFilters'
import {
	FATNESS_CATEGORY_TABS,
	FatnessCategoryFilter,
	getFatnessEmptyMessage,
	getFatnessScoreOptions,
} from '../shared/fatnessTableFilters'

const FatnessList = props => {
	const { activeFilter } = props
	const [fatnessPoint, setFatnessPoint] = useState<number[]>([])
	const [page, setPage] = useState<number>(1)
	const [selectedCategory, setSelectedCategory] =
		useState<FatnessCategoryFilter>(FatnessCategoryFilter.All)
	const [debouncedSearch, handleSearchChange, searchValue] = useDebounce(
		'',
		500,
	)

	const initialPageSize = 36
	const params = useMemo(() => {
		const nextParams = {
			...defaultInitialState,
			fatness: { ...defaultInitialState.fatness },
			lactation: { ...defaultInitialState.lactation },
			tag_id_search: debouncedSearch,
		}

		if (
			selectedCategory !== FatnessCategoryFilter.All &&
			selectedCategory !== FatnessCategoryFilter.WithoutNorm
		) {
			nextParams.fatness.statuses = [selectedCategory]
		}

		if (selectedCategory === FatnessCategoryFilter.WithoutNorm) {
			nextParams.lactation.startOf = 366
			nextParams.lactation.endOf = 100000
		}

		return nextParams
	}, [debouncedSearch, selectedCategory])
	const fatnessScoreOptions = useMemo(
		() => getFatnessScoreOptions(selectedCategory),
		[selectedCategory],
	)

	useEffect(() => {
		setPage(1)
	}, [activeFilter, debouncedSearch, fatnessPoint, selectedCategory])

	const prop = {
		page,
		fatnessPoint,
		activeFilter,
		params,
		tag_id_search: debouncedSearch,
		initialPageSize,
		isFetchCowsListEnabled: true,
		isFetchComplete: true,
	}
	const { cowsList, totalCountPages, fetchingCowsList } = useCowsNextPage(prop)
	const visibleCowsList = useMemo(
		() => filterCowsWithFarmCowNumber(cowsList),
		[cowsList],
	)
	const { data: visibleTotalCount } = useVisibleCowsCount({
		activeFilter,
		fatnessPoint,
		params,
		tag_id_search: debouncedSearch,
	})
	const paginationTotal =
		visibleTotalCount != null ? visibleTotalCount : totalCountPages
	const { isFetchReport, setIsFetchReport } = useExportFatnessReport({
		page,
		activeFilter,
		initialPageSize,
		fatnessPoint,
		params,
	})

	return (
		<CardStandart
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				padding: '24px',
			}}
		>
			<Box
				display='flex'
				alignItems='center'
				justifyContent='space-between'
				mb='16px'
			>
				<TypographyHeader>Список голов</TypographyHeader>
				<Box display='flex' alignItems='center' gap='16px'>
					<Input
						width='350px'
						value={searchValue}
						onChange={handleSearchChange}
						label=''
						placeholder='Поиск по номеру бирки и ошейника'
						size='small'
						startIcon={<SearchIcon stroke={COLORS.icon} />}
						inputProps={{ style: { paddingLeft: '4px' } }}
					/>
					<TooltipHover
						title='Экспорт таблицы'
						placement='bottom-end'
						oneLine
						disableHoverBackground
					>
						<IconButton
							onClick={() => setIsFetchReport(true)}
							size='small'
							sx={{
								borderRadius: '8px',
								'&:hover': {
									backgroundColor: COLORS.background,
								},
							}}
						>
							{isFetchReport ? (
								<Preloader color={COLORS.icon} size={24} />
							) : (
								<DownloadLogo stroke={COLORS.icon} />
							)}
						</IconButton>
					</TooltipHover>
				</Box>
			</Box>
			<Box mb='16px'>
				<TabsFilter
					availableTabs={FATNESS_CATEGORY_TABS}
					selectedKey={selectedCategory}
					onClick={key => setSelectedCategory(key as FatnessCategoryFilter)}
				/>
			</Box>
			<FatnessTable
				cattlesInfo={visibleCowsList}
				setActiveFilterTypeSelect={setFatnessPoint}
				fetchingCowsList={fetchingCowsList}
				fatnessScoreOptions={fatnessScoreOptions}
				emptyMessage={getFatnessEmptyMessage(
					selectedCategory,
					fatnessPoint.length > 0 || debouncedSearch.length > 0,
				)}
				showFilteredEmptyState={
					fatnessPoint.length > 0 || debouncedSearch.length > 0
				}
			/>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '16px',
				}}
			>
				<Divider />
				<CustomPagination
					onChangeFromServer={setPage}
					count={36}
					totalCount={paginationTotal}
				/>
			</Box>
		</CardStandart>
	)
}

export default FatnessList
